import * as React from 'react';

import {
  Avatar,
  Badge,
  cn,
  Col,
  ComplexKeyboardShortcut,
  Kbd,
  Row,
  Tab,
  TouchTextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideArrowLeft } from 'lucide-react';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import { $scaleTicketShowExtras } from '~/modules/trade/_state/scaleTicketSettings.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { ScaleTicketTab } from '~/modules/trade/types/scaleTickets';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';
import { SelectPurchaseOrderTouch } from '~/shared/components/select-fields/SelectPurchaseOrderTouch';
import { SelectSalesOrderTouch } from '~/shared/components/select-fields/SelectSalesOrderTouch';
import { SelectServiceTouch } from '~/shared/components/select-fields/SelectServiceTouch';

const FIELD_LABEL_CLASS =
  'text-[0.563rem] text-neutral/60 uppercase font-medium';

const noop = () => undefined;

type TabLabelProps = {
  label: string;
  badge?: number;
  shortcut?: ComplexKeyboardShortcut;
};

type DocumentNumberProps = {
  documentNumber?: string;
  relatedOrderDocumentNumber?: string;
};

//
// ⚛️ ⚛️ ⚛️ TAB LABEL ⚛️ ⚛️ ⚛️
//
const TabLabel = ({ label, badge, shortcut }: TabLabelProps) => (
  <Row className="@TabLabel items-center gap-1.5">
    {badge != null && badge > 0 && (
      <Badge color="primary" size="xs" className="px-1.5 py-0.5">
        {badge}
      </Badge>
    )}
    <span className="font-medium">{label}</span>
    {shortcut && <Kbd variant="outline" shortcut={shortcut} />}
  </Row>
);

//
// ⚛️ ⚛️ ⚛️ DOCUMENT NUMBER ⚛️ ⚛️ ⚛️
//
export const DocumentNumber: React.FC<
  React.PropsWithChildren<DocumentNumberProps>
> = ({ documentNumber, relatedOrderDocumentNumber }) => (
  <Row gap2 className="@DocumentNumber font-medium text-neutral/60 uppercase">
    {documentNumber != null && `#${String(documentNumber).padStart(6, '0')} `}

    {relatedOrderDocumentNumber && (
      <Row gap2 yCenter className="font-normal">
        <LucideArrowLeft size={10} /> {relatedOrderDocumentNumber}
      </Row>
    )}
  </Row>
);

type TicketAccountDetailsProps = {
  avatarName: string;
};

//
// ⚛️ ⚛️ ⚛️ TICKET ACCOUNT DETAILS ROW ⚛️ ⚛️ ⚛️
//
export const TicketAccountDetailsRow: React.FC<
  React.PropsWithChildren<TicketAccountDetailsProps>
> = ({ avatarName, children }) => (
  <Row gap2 yCenter className="@TicketAccountDetailsRow w-full">
    <Avatar size="xl" variant="profile" name={avatarName} className="!h-13" />

    {children}
  </Row>
);

type FormFieldValue = string | null;

type NotesFields = {
  internalNotes: string | null;
  externalNotes: string | null;
};

interface TicketDetailsRowProps<TForm extends Record<string, FormFieldValue>> {
  fields: TForm;
  ticket: { id: string } | null;
  onUpdateFields: ({
    field,
    value,
    ticketId,
  }: {
    field: keyof TForm;
    value: string;
    ticketId: string;
  }) => void;
  notesFields: NotesFields;
  onUpdateNotesField: ({
    field,
    value,
    ticketId,
  }: {
    field: keyof NotesFields;
    value: string;
    ticketId: string;
  }) => void;
  purchaseOrderId?: string | null;
  serviceId?: string | null;
  supplierAccountId?: string | null;
  supplierAddressId?: string | null;
  facilityId?: string | null;
  onPurchaseOrderChange?: (value: string | undefined) => void;
  onServiceChange?: (value: string | undefined) => void;
  salesOrderId?: string | null;
  customerAccountId?: string | null;
  onSalesOrderChange?: (value: string | undefined) => void;
  showPurchaseOrder?: boolean;
  showService?: boolean;
  showSalesOrder?: boolean;
  purchaseOrderDisabled?: boolean;
  serviceDisabled?: boolean;
  salesOrderDisabled?: boolean;
  leadingContent?: React.ReactNode;
  className?: string;
}

//
// ⚛️ ⚛️ ⚛️ TICKET DETAILS ROW ⚛️ ⚛️ ⚛️
//
export function TicketDetailsRow<TForm extends Record<string, FormFieldValue>>(
  props: TicketDetailsRowProps<TForm>,
) {
  const showExtras = useAtomValue($scaleTicketShowExtras);
  if (!showExtras) return null;
  return <TicketDetailsRowContent {...props} className="@TicketDetailsRow" />;
}

function TicketDetailsRowContent<TForm extends Record<string, FormFieldValue>>({
  ticket,
  fields,
  onUpdateFields,
  notesFields,
  onUpdateNotesField,
  purchaseOrderId,
  serviceId,
  supplierAccountId,
  supplierAddressId,
  facilityId,
  onPurchaseOrderChange,
  onServiceChange,
  salesOrderId,
  customerAccountId,
  onSalesOrderChange,
  showPurchaseOrder,
  showService,
  showSalesOrder,
  purchaseOrderDisabled,
  serviceDisabled,
  salesOrderDisabled,
  className,
}: TicketDetailsRowProps<TForm>) {
  const t = useTranslations();

  const setFocusedField = useSetAtom($focusedField);
  const focusPositions = useAtomValue($remoteFocusPositions);

  return (
    <Row
      xBetween
      gap2
      className={cn(
        '@TicketDetailsRowContent border-t border-neutral/10 px-3 py-2',
        className,
      )}
    >
      <Row xStart gap2 className="w-full flex-1">
        {showSalesOrder && (
          <Col gap1 className="min-w-35">
            <span className={FIELD_LABEL_CLASS}>
              {t.scaleTicketPro.formFields.salesOrder}
            </span>
            <FieldFocusIndicator
              fieldId="salesOrderId"
              focusPositions={focusPositions}
            >
              <div
                onFocus={() => setFocusedField('salesOrderId')}
                onBlur={() => setFocusedField(null)}
              >
                <SelectSalesOrderTouch
                  aria-label={t.scaleTicketPro.formFields.salesOrder}
                  value={salesOrderId}
                  onChange={onSalesOrderChange ?? noop}
                  isDisabled={salesOrderDisabled}
                  skipSearch={salesOrderDisabled}
                  placeholder={
                    t.scaleTicketPro.detail.selectSalesOrderPlaceholder
                  }
                  accountIds={
                    customerAccountId ? [customerAccountId] : undefined
                  }
                />
              </div>
            </FieldFocusIndicator>
          </Col>
        )}
        {showPurchaseOrder && (
          <Col gap1 className="min-w-35">
            <span className={FIELD_LABEL_CLASS}>
              {t.scaleTicketPro.formFields.poNumber}
            </span>
            <FieldFocusIndicator
              fieldId="purchaseOrderId"
              focusPositions={focusPositions}
            >
              <div
                onFocus={() => setFocusedField('purchaseOrderId')}
                onBlur={() => setFocusedField(null)}
              >
                <SelectPurchaseOrderTouch
                  aria-label={t.scaleTicketPro.formFields.poNumber}
                  value={purchaseOrderId}
                  onChange={onPurchaseOrderChange ?? noop}
                  isDisabled={purchaseOrderDisabled}
                  skipSearch={purchaseOrderDisabled}
                  placeholder={t.scaleTicketPro.formFields.notLinked}
                  accountIds={
                    supplierAccountId ? [supplierAccountId] : undefined
                  }
                  facilityIds={facilityId ? [facilityId] : undefined}
                />
              </div>
            </FieldFocusIndicator>
          </Col>
        )}
        {showService && (
          <Col gap1 className="min-w-35">
            <span className={FIELD_LABEL_CLASS}>
              {t.scaleTicketPro.formFields.jobNumber}
            </span>
            <FieldFocusIndicator
              fieldId="serviceId"
              focusPositions={focusPositions}
            >
              <div
                onFocus={() => setFocusedField('serviceId')}
                onBlur={() => setFocusedField(null)}
              >
                <SelectServiceTouch
                  aria-label={t.scaleTicketPro.formFields.jobNumber}
                  value={serviceId}
                  onChange={onServiceChange ?? noop}
                  isDisabled={serviceDisabled}
                  skipSearch={serviceDisabled}
                  placeholder={t.scaleTicketPro.formFields.notLinked}
                  accountIds={
                    supplierAccountId ? [supplierAccountId] : undefined
                  }
                  accountAddressIds={
                    supplierAddressId ? [supplierAddressId] : undefined
                  }
                />
              </div>
            </FieldFocusIndicator>
          </Col>
        )}

        <Col gap1 className="flex-1">
          <span className={FIELD_LABEL_CLASS}>
            {t.scaleTicketPro.formFields.truckNumber}
          </span>
          <FieldFocusIndicator
            fieldId="truckNumber"
            focusPositions={focusPositions}
          >
            <TouchTextField
              aria-label={t.scaleTicketPro.formFields.truckNumber}
              className="!h-[44px]"
              value={fields.truckNumber ?? undefined}
              onChange={(value) => {
                if (!ticket?.id) return;
                onUpdateFields({
                  field: 'truckNumber',
                  value: value || '',
                  ticketId: ticket.id,
                });
              }}
              onFocus={() => setFocusedField('truckNumber')}
              onBlur={() => setFocusedField(null)}
            />
          </FieldFocusIndicator>
        </Col>
        <Col gap1 className="flex-1">
          <span className={FIELD_LABEL_CLASS}>
            {t.scaleTicketPro.formFields.licensePlate}
          </span>
          <FieldFocusIndicator
            fieldId="vehicleLicensePlate"
            focusPositions={focusPositions}
          >
            <TouchTextField
              aria-label={t.scaleTicketPro.formFields.licensePlate}
              className="!h-[44px]"
              value={fields.vehicleLicensePlate ?? undefined}
              onChange={(value) => {
                if (!ticket?.id) return;
                onUpdateFields({
                  field: 'vehicleLicensePlate',
                  value: value || '',
                  ticketId: ticket.id,
                });
              }}
              onFocus={() => setFocusedField('vehicleLicensePlate')}
              onBlur={() => setFocusedField(null)}
            />
          </FieldFocusIndicator>
        </Col>
        <Col gap1 className="flex-2">
          <span className={FIELD_LABEL_CLASS}>
            {t.scaleTicketPro.formFields.internalNotes}
          </span>
          <FieldFocusIndicator
            fieldId="internalNotes"
            focusPositions={focusPositions}
          >
            <TouchTextField
              aria-label={t.scaleTicketPro.formFields.internalNotes}
              className="!h-[44px]"
              value={notesFields.internalNotes ?? undefined}
              onChange={(value) => {
                if (!ticket?.id) return;
                onUpdateNotesField({
                  field: 'internalNotes',
                  value: value || '',
                  ticketId: ticket.id,
                });
              }}
              onFocus={() => setFocusedField('internalNotes')}
              onBlur={() => setFocusedField(null)}
            />
          </FieldFocusIndicator>
        </Col>
        <Col gap1 className="flex-2">
          <span className={FIELD_LABEL_CLASS}>
            {t.scaleTicketPro.formFields.externalNotes}
          </span>
          <FieldFocusIndicator
            fieldId="externalNotes"
            focusPositions={focusPositions}
          >
            <TouchTextField
              aria-label={t.scaleTicketPro.formFields.externalNotes}
              className="!h-[44px]"
              value={notesFields.externalNotes ?? undefined}
              onChange={(value) => {
                if (!ticket?.id) return;
                onUpdateNotesField({
                  field: 'externalNotes',
                  value: value || '',
                  ticketId: ticket.id,
                });
              }}
              onFocus={() => setFocusedField('externalNotes')}
              onBlur={() => setFocusedField(null)}
            />
          </FieldFocusIndicator>
        </Col>
      </Row>
    </Row>
  );
}

const INBOUND_TAB_KEYS: ScaleTicketTab[] = [
  'materials',
  'notes',
  'photos',
  'supplierInfo',
  'details',
  'documents',
  'history',
];

const OUTBOUND_TAB_KEYS: ScaleTicketTab[] = [
  'materials',
  'notes',
  'photos',
  'customerInfo',
  'details',
  'documents',
  'history',
];

type TicketTabsRowProps = {
  context: 'inbound' | 'outbound';
  activeTab: string;
  onTabChange: (tab: string) => void;
  materialCount?: number;
  rightContent?: React.ReactNode;
};

//
// ⚛️ ⚛️ ⚛️ TICKET TABS ROW ⚛️ ⚛️ ⚛️
//
export const TicketTabsRow: React.FC<TicketTabsRowProps> = ({
  context,
  activeTab,
  onTabChange,
  materialCount,
  rightContent,
}) => {
  const t = useTranslations();

  const tabKeys = context === 'inbound' ? INBOUND_TAB_KEYS : OUTBOUND_TAB_KEYS;

  const tabs = React.useMemo(
    () =>
      [
        {
          key: 'materials',
          label: (
            <TabLabel
              label={t.scaleTicketPro.tabs.materials}
              badge={materialCount}
              shortcut="Meta+3"
            />
          ),
        },
        {
          key: 'notes',
          label: (
            <TabLabel label={t.scaleTicketPro.tabs.notes} shortcut="Meta+4" />
          ),
        },
        { key: 'photos', label: t.scaleTicketPro.tabs.photos },
        { key: 'supplierInfo', label: t.scaleTicketPro.tabs.supplierInfo },
        { key: 'customerInfo', label: t.scaleTicketPro.tabs.customerInfo },
        { key: 'details', label: t.scaleTicketPro.tabs.details },
        { key: 'documents', label: t.scaleTicketPro.tabs.documents },
        { key: 'files', label: t.scaleTicketPro.tabs.files },
        { key: 'reconciliation', label: t.scaleTicketPro.tabs.reconciliation },
        { key: 'history', label: t.scaleTicketPro.tabs.history },
        { key: 'compliance', label: t.scaleTicketPro.tabs.compliance },
      ].filter((tab) => tabKeys.includes(tab.key as ScaleTicketTab)),
    [t, materialCount, tabKeys],
  );

  return (
    <Row
      xBetween
      yCenter
      className="@TicketTabsRow border-t border-neutral/10 px-4"
    >
      <Row gap3>
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            selected={activeTab === tab.key}
            onClick={() => onTabChange(tab.key)}
            variant="primary"
          >
            {tab.label}
          </Tab>
        ))}
      </Row>
      {rightContent && (
        <Row gap2 yCenter>
          {rightContent}
        </Row>
      )}
    </Row>
  );
};
